import { useCallback, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function useClinicalTemplateModal({
  templateCode,
  clinicId,
  patientId,
  isReadOnly,
  readOnlyReason,
  getInitialForm,
  mapInstanceToForm,
  validateForm,
  buildTitle,
  buildSummary,
  successMessage,
  onSaved,
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(() => getInitialForm())
  const [latestInstance, setLatestInstance] = useState(null)

  const resetFormState = useCallback(() => {
    setLatestInstance(null)
    setForm(getInitialForm())
  }, [getInitialForm])

  const loadLatestInstance = useCallback(async () => {
    try {
      if (!patientId || !clinicId) {
        resetFormState()
        return null
      }

      setLoading(true)

      const params = new URLSearchParams({
        patientId,
        clinicId,
        templateCode,
      })

      const res = await apiFetch(
        `${API_BASE}/clinical-history/form-instances?${params.toString()}`
      )
      const data = await res.json().catch(() => [])

      if (!res.ok) {
        resetFormState()
        return null
      }

      const rows = Array.isArray(data) ? data : []
      const latest = rows.length ? rows[0] : null

      setLatestInstance(latest)
      setForm(mapInstanceToForm(latest))
      return latest
    } catch {
      resetFormState()
      return null
    } finally {
      setLoading(false)
    }
  }, [
    patientId,
    clinicId,
    templateCode,
    mapInstanceToForm,
    resetFormState,
  ])

  const openModal = useCallback(async () => {
    await loadLatestInstance()
    setOpen(true)
  }, [loadLatestInstance])

  const closeModal = useCallback(() => {
    setOpen(false)
  }, [])

  const saveInstance = useCallback(async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason)
        return null
      }

      if (!patientId || !clinicId) {
        alert("Falta contexto de paciente o clÃ­nica.")
        return null
      }

      const validationError =
        typeof validateForm === "function" ? validateForm(form) : ""

      if (validationError) {
        alert(validationError)
        return null
      }

      setSaving(true)

      const res = await apiFetch(`${API_BASE}/clinical-history/form-instances`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clinicId,
          patientId,
          templateCode,
          formInstanceId: latestInstance?.id || undefined,
          source: "DOCTOR",
          status: "DRAFT",
          title: buildTitle(form),
          summary: buildSummary(form),
          dataJson: form,
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || `No se pudo guardar ${templateCode}.`)
        return null
      }

      setLatestInstance(data || null)
      alert(successMessage || "Guardado correctamente.")

      if (typeof onSaved === "function") {
        await onSaved(data || null, form)
      }

      setOpen(false)
      return data || null
    } catch {
      alert(`Error al guardar ${templateCode}.`)
      return null
    } finally {
      setSaving(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    patientId,
    clinicId,
    templateCode,
    latestInstance,
    form,
    validateForm,
    buildTitle,
    buildSummary,
    successMessage,
    onSaved,
  ])

  return {
    open,
    setOpen,
    loading,
    saving,
    form,
    setForm,
    latestInstance,
    setLatestInstance,
    resetFormState,
    loadLatestInstance,
    openModal,
    closeModal,
    saveInstance,
  }
}
